import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { Terminal } from 'xterm';
import chalk from 'chalk';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { MatDialog } from '@angular/material/dialog';
import { parser } from '../../services/pig-parser';
import { LabServicesResource } from '../resources/lab-services.resource';
import { XplentyConsoleShortcutsComponent } from './xplenty-console-shortcuts.component';
import { XplentyConsoleFunctionsComponent } from './xplenty-console-functions.component';

const options: any = { enabled: true, level: 2 };
const forcedChalk = new chalk.Instance(options);

const ENTER = '\r';
const NEW_LINE = '\n';
const BACKSPACE = '\u007F';
const ARROW_UP = '\x1b[A';
const ARROW_DOWN = '\x1b[B';
const ARROW_RIGHT = '\x1b[C';
const ARROW_LEFT = '\x1b[D';
const TAB = '\t';
const SHIFT_TAB = '\x1b[Z';
const availableShortcuts = ['c', 'v', 'r'];

// eslint-disable-next-line import/no-mutable-exports
export let GLOBAL_TERM_REFERENCE: Terminal = null;

@Component({
  selector: 'xplenty-console',
  template: `
    <div class="console-container">
      <div class="browser-bar">
        <div class="actions">
          <div class="dot red"></div>
          <div class="dot yellow"></div>
          <div class="dot green"></div>
        </div>
      </div>
      <div id="terminal"></div>
      <a rel="tooltip" (click)="openShortcutsDialog()" class="btn btn-small shortcuts-btn"
        ><i class="icon fa fa-keyboard-o"></i
      ></a>
      <a rel="tooltip" (click)="openFunctionsDialog()" class="btn btn-small functions-btn"
        ><i class="icon fa fa-code"></i
      ></a>
    </div>
  `,
})
export class XplentyConsoleComponent implements AfterViewInit, OnDestroy {
  private isDialogOpen = false;
  private isTermInitialized = false;
  private terminalValue = [''];
  private term: Terminal;
  private commandsCache: Array<string[]> = [['']];
  private commandCacheCurrentIndex = 0;
  private cursorPosition = 0;
  private isCtrlKeyPressed = false;
  private isShiftKeyPressed = false;
  private keyDownListenerRef: any = null;
  private keyUpListenerRef: any = null;
  private resizeListenerRef: any = null;
  private currentRow = 0;
  private fitAddon: FitAddon;

  constructor(private LabServices: LabServicesResource, private dialog: MatDialog) {}

  ngAfterViewInit() {
    this.term = new Terminal({
      cursorBlink: true,
      scrollback: 1000,
      tabStopWidth: 4,
      cursorStyle: 'bar',
      fontSize: 16,
      lineHeight: 1,
    });
    GLOBAL_TERM_REFERENCE = this.term;
    this.fitAddon = new FitAddon();
    this.term.loadAddon(this.fitAddon);
    this.term.open(document.getElementById('terminal')!);
    this.fitAddon.fit();

    this.runFakeTerminal();
    this.term.focus();
    this.keyDownListenerRef = this.handleKeyDown.bind(this);
    this.keyUpListenerRef = this.handleKeyUp.bind(this);
    this.resizeListenerRef = this.handleResize.bind(this);
    document.addEventListener('keydown', this.keyDownListenerRef);
    document.addEventListener('keyup', this.keyUpListenerRef);
    window.addEventListener('resize', this.resizeListenerRef);
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.keyDownListenerRef);
    document.removeEventListener('keyup', this.keyUpListenerRef);
    window.removeEventListener('resize', this.resizeListenerRef);
    GLOBAL_TERM_REFERENCE = null;
  }

  public openShortcutsDialog() {
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(XplentyConsoleShortcutsComponent, {
      data: {},
      position: {
        top: '150px',
      },
      maxWidth: '500px',
      panelClass: 'shortcuts-dialog',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isDialogOpen = false;
      this.term.focus();
    });
  }

  public openFunctionsDialog() {
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(XplentyConsoleFunctionsComponent, {
      data: {},
      position: {
        top: '150px',
      },
      maxWidth: '500px',
      panelClass: 'functions-dialog',
    });

    dialogRef.afterClosed().subscribe(async (functionExample: string) => {
      if (functionExample) {
        const functions = functionExample.split(NEW_LINE);
        // eslint-disable-next-line no-restricted-syntax
        for (const example of functions) {
          this.setTermInput(example);
          // eslint-disable-next-line no-await-in-loop
          await this.handleEnterKey(ENTER);
        }
      }
      this.isDialogOpen = false;
      this.term.focus();
    });
  }

  private get lastTerminalValue(): string {
    return this.terminalValue[this.terminalValue.length - 1];
  }

  private runFakeTerminal(): void {
    if (this.isTermInitialized) {
      return;
    }

    this.isTermInitialized = true;

    this.term.writeln('Welcome to X-Console');
    this.term.writeln('Type or paste your expressions here to evaluate them and see the results.');
    this.term.writeln('You can use either literal values or variables as function arguments. For example: ');
    this.term.writeln('');
    this.prompt();
    this.term.writeln("name = 'President John Smith'");
    this.addCommandToCache(["name = 'President John Smith'"]);
    this.prompt();
    this.term.writeln("REPLACE(name,'President','Ex-President')");
    this.addCommandToCache(["REPLACE(name,'President','Ex-President')"]);
    this.prompt();

    this.term.onData((e: string) => {
      const code = e.charCodeAt(0);
      switch (e) {
        case ENTER: // Enter
          this.handleEnterKey(e);
          break;
        case BACKSPACE: // Backspace (DEL)
          this.handleBackspaceKey();
          break;
        case ARROW_UP: // Arrow up
          this.handleArrowUpKey();
          break;
        case ARROW_DOWN: // Arrow down
          this.handleArrowDownKey();
          break;
        case ARROW_RIGHT: // Arrow right
          this.handleArrowRightKey(e);
          break;
        case ARROW_LEFT: // Arrow left
          this.handleArrowLeftKey(e);
          break;
        case TAB: // Tab
          this.handleIndent();
          break;
        case SHIFT_TAB: // Shift + Tab
          this.handleRemoveIndent();
          break;
        default:
          if (code > 31 && code < 127) {
            this.setTermInput(e);
          }
      }
    });
  }

  private addCommandToCache(command: string[]) {
    this.commandsCache.splice(1, 0, command.slice());
  }

  private prompt(): void {
    this.term.write('\r$ ');
  }

  private typeMultipleTimes(data: string, times: number): void {
    for (let i = 0; i < times; i += 1) {
      this.term.write(data);
    }
  }

  private resetCursorPosition() {
    const cursorDiff = this.terminalValue[this.currentRow].length - this.cursorPosition;
    this.typeMultipleTimes(ARROW_RIGHT, cursorDiff);
  }

  private setPreviousCursorPosition() {
    const rowsDiff = this.terminalValue.length - 1 - this.currentRow;
    if (rowsDiff > 0) {
      this.typeMultipleTimes(ARROW_UP, rowsDiff);
    }

    if (this.currentRow !== this.terminalValue.length - 1 && this.terminalValue[this.currentRow]) {
      const lengthDiff = this.terminalValue[this.currentRow].length - this.lastTerminalValue.length;
      if (lengthDiff < 0) {
        this.typeMultipleTimes(ARROW_LEFT, Math.abs(lengthDiff));
      } else {
        this.typeMultipleTimes(ARROW_RIGHT, lengthDiff);
      }
    }

    const cursorDiff = this.terminalValue[this.currentRow].length - this.cursorPosition;
    if (cursorDiff < 0) {
      this.typeMultipleTimes(ARROW_RIGHT, Math.abs(cursorDiff + 1));
    } else {
      this.typeMultipleTimes(ARROW_LEFT, cursorDiff);
    }
  }

  private goToTheEnd(): number {
    const rowsDiff = this.terminalValue.length - 1 - this.currentRow;
    let rightBuffer = 0;
    if (rowsDiff > 0) {
      const cursorPositionFromRight = this.terminalValue[this.currentRow].length - this.cursorPosition;
      const charsDiffInRows =
        this.terminalValue[this.currentRow].length - this.lastTerminalValue.length - cursorPositionFromRight;
      if (charsDiffInRows > 0) {
        this.typeMultipleTimes(ARROW_LEFT, charsDiffInRows);
      } else {
        rightBuffer = Math.abs(charsDiffInRows);
        this.typeMultipleTimes(ARROW_RIGHT, rightBuffer);
      }

      this.typeMultipleTimes(ARROW_DOWN, rowsDiff);
    }

    return rightBuffer;
  }

  private clearCurrentCommand(): void {
    const rightBuffer = this.goToTheEnd();

    for (let i = this.terminalValue.length - 1; i >= 0; i -= 1) {
      const row = this.terminalValue[i];
      const bufferX = this.term.buffer.active.cursorX;
      const isLastRow = i === this.terminalValue.length - 1;
      let goToRight = isLastRow ? row.length + 2 - bufferX : row.length;
      if (isLastRow && rightBuffer) {
        goToRight = 0;
      }

      this.typeMultipleTimes(ARROW_RIGHT, goToRight);
      this.typeMultipleTimes('\b \b', row.length);

      if (i !== 0) {
        this.term.write(ARROW_UP);
      }
    }
  }

  private handleResize(): void {
    this.clearCurrentCommand();
    this.fitAddon.fit();
    this.splitTerminalValue();
    this.writeTerminalValue();
    this.currentRow += Math.floor(this.cursorPosition / this.cols);
    this.cursorPosition %= this.cols;
    this.setPreviousCursorPosition();
  }

  private handleKeyUp(e: KeyboardEvent): boolean {
    if (this.isDialogOpen) {
      return true;
    }

    e.preventDefault();
    this.isCtrlKeyPressed = e.ctrlKey || e.metaKey;
    this.isShiftKeyPressed = e.shiftKey;
    return false;
  }

  private handleKeyDown(e: KeyboardEvent): boolean {
    if (this.isDialogOpen) {
      return true;
    }

    this.isCtrlKeyPressed = e.ctrlKey || e.metaKey;
    this.isShiftKeyPressed = e.shiftKey;

    if (this.isCtrlKeyPressed && availableShortcuts.includes(e.key)) {
      return true;
    }

    e.preventDefault();

    if (this.isCtrlKeyPressed && e.key === 's') {
      this.openShortcutsDialog();
    }

    if (this.isCtrlKeyPressed && e.key === 'g') {
      this.openFunctionsDialog();
    }

    if (this.isCtrlKeyPressed && e.key === 'l') {
      this.term.clear();
    }

    if (this.isCtrlKeyPressed && e.key === 'z') {
      this.cancelCommand();
    }

    if (this.isCtrlKeyPressed && e.key === 'a') {
      this.moveCursorToBegin();
    }

    if (this.isCtrlKeyPressed && e.key === 'e') {
      this.moveCursorToEnd();
    }

    if (this.isCtrlKeyPressed && e.key === 'ArrowUp') {
      this.moveCursorUp();
    }

    if (this.isCtrlKeyPressed && e.key === 'ArrowDown') {
      this.moveCursorDown();
    }

    if (e.key === ' ') {
      this.setTermInput(e.key);
    }

    return false;
  }

  private moveCursorDown() {
    if (this.currentRow < this.terminalValue.length - 1) {
      this.term.write(ARROW_DOWN);
      this.currentRow += 1;

      const lengthDiff = this.cursorPosition - this.terminalValue[this.currentRow].length;
      if (lengthDiff > 0) {
        for (let i = 0; i < lengthDiff; i += 1) {
          this.term.write(ARROW_LEFT);
          this.cursorPosition -= 1;
        }
      }
    }
  }

  private moveCursorUp() {
    if (this.currentRow > 0) {
      this.term.write(ARROW_UP);
      this.currentRow -= 1;

      const lengthDiff = this.cursorPosition - this.terminalValue[this.currentRow].length;
      if (lengthDiff > 0) {
        for (let i = 0; i < lengthDiff; i += 1) {
          this.term.write(ARROW_LEFT);
          this.cursorPosition -= 1;
        }
      }
    }
  }

  private moveCursorToBegin(): void {
    if (this.cursorPosition > 0) {
      this.setPreviousCursorPosition();
      this.cursorPosition = 0;
    }
  }

  private moveCursorToEnd(): void {
    if (this.cursorPosition < this.terminalValue[this.currentRow].length) {
      this.resetCursorPosition();
      this.cursorPosition = this.terminalValue[this.currentRow].length;
    }
  }

  private cancelCommand(): void {
    if (this.terminalValue[0] !== '' && this.commandsCache[1].join() !== this.terminalValue.join()) {
      this.addCommandToCache(this.terminalValue);
    }
    this.cursorPosition = 0;
    this.terminalValue = [''];
    this.currentRow = 0;
    this.prompt();
  }

  private get cols(): number {
    return this.term.cols - 3;
  }

  private handleBackspaceKey(): void {
    if (this.terminalValue[this.currentRow] && this.cursorPosition > 0) {
      this.clearCurrentCommand();
      const terminalValue = this.terminalValue[this.currentRow];
      this.terminalValue[this.currentRow] =
        terminalValue.substr(0, this.cursorPosition - 1) + terminalValue.substr(this.cursorPosition);

      if (terminalValue.length === this.cols) {
        this.splitTerminalValue();
      }

      this.writeTerminalValue();
      this.cursorPosition -= 1;
      this.setPreviousCursorPosition();
    }
  }

  private typeInTerminal(text: string[], { saveCursorPosition = false } = {}): void {
    this.clearCurrentCommand();
    this.terminalValue = text;
    this.writeTerminalValue();

    if (saveCursorPosition) {
      return;
    }

    this.cursorPosition = text[text.length - 1].length;
    this.currentRow = text.length - 1;
  }

  private handleArrowUpKey(): void {
    if (this.commandCacheCurrentIndex < this.commandsCache.length - 1) {
      this.commandCacheCurrentIndex += 1;

      const command = this.commandsCache[this.commandCacheCurrentIndex];
      this.typeInTerminal(command);
    }
  }

  private handleArrowDownKey(): void {
    if (this.commandCacheCurrentIndex > 0) {
      this.commandCacheCurrentIndex -= 1;
      const command = this.commandsCache[this.commandCacheCurrentIndex];
      this.typeInTerminal(command);
    }
  }

  private handleArrowRightKey(e: string): void {
    if (this.cursorPosition < this.terminalValue[this.currentRow].length) {
      this.cursorPosition += 1;
      this.term.write(e);
    }
  }

  private handleArrowLeftKey(e: string): void {
    if (this.cursorPosition > 0) {
      this.cursorPosition -= 1;
      this.term.write(e);
    }
  }

  private handleIndent(): void {
    const newValue = this.terminalValue.slice();
    newValue[this.currentRow] = `    ${this.terminalValue[this.currentRow]}`;
    this.typeInTerminal(newValue, { saveCursorPosition: true });
    this.cursorPosition += 4;
    this.setPreviousCursorPosition();
  }

  private handleRemoveIndent(): void {
    const terminalValue = this.terminalValue[this.currentRow];
    if (terminalValue.substr(0, 3) === '   ') {
      const newValue = this.terminalValue.slice();
      newValue[this.currentRow] = terminalValue.slice(4, this.terminalValue.length);
      this.typeInTerminal(newValue, { saveCursorPosition: true });
      this.cursorPosition -= 4;
      this.setPreviousCursorPosition();
    }
  }

  private handleNextLine(): void {
    this.currentRow += 1;
    this.term.write('\r\n  ');
    if (!this.terminalValue[this.currentRow]) {
      this.terminalValue.push('');
    }
    this.cursorPosition = 0;
  }

  private async handleEnterKey(e: string): Promise<null> {
    if (this.isShiftKeyPressed) {
      this.handleNextLine();
      return null;
    }

    if (this.terminalValue.join() === '') {
      this.term.write(NEW_LINE);
      this.prompt();
      return null;
    }

    this.goToTheEnd();

    this.commandCacheCurrentIndex = 0;

    if (this.terminalValue[0] !== '' && this.commandsCache[1].join() !== this.terminalValue.join()) {
      this.addCommandToCache(this.terminalValue);
    }
    this.cursorPosition = 0;
    this.term.writeln('');

    try {
      const parserResult = (parser as any).parse(this.terminalValue.join(''));

      const response = await this.LabServices.evaluate(parserResult);

      const { result, error } = response;

      if (!error) {
        if (response.callback !== 'undefined') {
          // Call the callback function with the result
          // eslint-disable-next-line
          new Function(response.callback)(response.result_object);
        }
        this.term.write(forcedChalk.yellowBright(`==> ${result}\n`));
      } else if (typeof error === 'string') {
        const errorText = error.split(NEW_LINE);
        errorText.forEach((item: string) => {
          this.term.writeln(forcedChalk.redBright(item));
        });
      } else {
        this.term.writeln(forcedChalk.redBright(`ERROR: Network error occurred.`));
      }
    } catch (error) {
      if (error instanceof Error) {
        const errorText = error.toString().split(NEW_LINE);
        errorText[0] = `ERROR: ${errorText[0]}`;
        errorText.forEach((item) => {
          this.term.writeln(forcedChalk.redBright(item));
        });
      }
    }

    this.prompt();

    if (e !== '\r') {
      this.terminalValue[this.currentRow] = e;
    } else {
      this.terminalValue = [''];
    }
    this.currentRow = 0;

    return null;
  }

  private splitTerminalValue() {
    const terminalValueSplitted = this.terminalValue.join('').match(new RegExp(`.{1,${this.cols}}`, 'g')) || [''];
    this.terminalValue = [...terminalValueSplitted];
  }

  private setTermInput(e: string): void {
    this.clearCurrentCommand();
    const terminalValue = this.terminalValue[this.currentRow];

    const futureRowLength = e.length + terminalValue.length;

    if (e.includes(NEW_LINE) || e.includes(ENTER)) {
      this.terminalValue = e.includes(NEW_LINE) ? e.split(NEW_LINE) : e.split(ENTER);
      this.terminalValue = this.terminalValue.map((line) => `${line} `);
      this.writeTerminalValue();
      this.currentRow = this.terminalValue.length - 1;
      this.cursorPosition = this.lastTerminalValue.length;
    } else {
      this.terminalValue[this.currentRow] =
        terminalValue.substr(0, this.cursorPosition) + e + terminalValue.substr(this.cursorPosition);

      if (futureRowLength > this.cols) {
        this.splitTerminalValue();

        this.writeTerminalValue();
        this.currentRow += Math.floor((this.cursorPosition + e.length) / this.cols);
        this.cursorPosition = (this.cursorPosition + e.length) % this.cols;
      } else {
        this.writeTerminalValue();
        this.cursorPosition += e.length;
      }
    }

    this.setPreviousCursorPosition();
  }

  private writeTerminalValue(): void {
    this.terminalValue.forEach((row, index) => {
      this.term.write(row);
      if (this.terminalValue.length > 1 && index !== this.terminalValue.length - 1) {
        this.term.write(NEW_LINE);
        this.typeMultipleTimes(ARROW_LEFT, row.length);
      }
    });
  }
}

((window as any).xplenty as angular.IModule).directive(
  'xplentyConsole',
  downgradeComponent({
    component: XplentyConsoleComponent,
  }),
);
